import React from "react";
import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowUpRight,
  CircleDollarSign,
  Clock,
  ExternalLink,
  GitBranch,
  Network,
  UserCheck,
} from "lucide-react";
import moment from "moment";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type EnrichmentEntity,
  contentTypeLabel,
  isEnrichmentContentType,
} from "@/lib/tina/content-types";
import { cn } from "@/lib/utils";
import { ConfidenceMeter } from "@/pages/events/components/confidence-meter";
import { ResolvedByChip } from "@/pages/tina/components/resolved-by-chip";
import { RunStatusBadge } from "@/pages/tina/components/run-status-badge";
import { useEntityTrace } from "@/pages/tina/hooks/use-entity-trace";
import {
  STATUS_ACCENT,
  type StatusAccent,
} from "@/pages/tina/items/components/status-accent";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { type TraceSource, readTaskName } from "@/pages/tina/lib/entity-trace";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import { CostBreakdownDialog } from "@/pages/tina/runs/components/cost-breakdown-dialog";
import { useRunsRepository } from "@/pages/tina/runs/hooks/use-runs-repository";
import type { ItemStatus } from "@/types/tina";

/**
 * "Tina Trace" - the Tina item behind a Mood entity.
 *
 * Generalised from the events-only `EventTinaTrace`, which read `metadata.id`
 * and nothing else. Every Tina-*created* and Tina-*enriched* entity gets one
 * now; finding the item is `useEntityTrace`'s job, and the precedence rule that
 * makes it correct is argued in `lib/entity-trace.ts`.
 *
 * Renders nothing when no pointer resolves. An entity Tina never touched has no
 * trace, and an empty card would read as a broken one.
 *
 * **Never rendered for genre.** Genre enrichment persists flat on the genre
 * document, which has no `taskName` field, so there is nothing to point at -
 * deliberate.
 */
interface EntityTinaTraceProps {
  /** Which of the nine entity flows this record belongs to. */
  entityType: EnrichmentEntity;
  /** The Mood document id - the `entity_id` a Tina enrichment job carries. */
  entityId: string | null | undefined;
  /** The entity document's `metadata` block, if it has one. */
  metadata?: unknown;
  /**
   * The entity's `enrichment` object, whatever shape the read schema gave it.
   * Only `taskName` is read off it, but taking the whole object keeps the
   * `.passthrough()` cast in one place instead of at every call site.
   */
  enrichment?: unknown;
  /** Skip the `GET /events?entity_id=` tier. See {@link useEntityTrace}. */
  disableLookup?: boolean;
}

/** What the pointer means, spelled out - the two answers are not the same. */
const PROVENANCE_LABEL: Record<TraceSource, string> = {
  metadata_stamp: "Created by this run",
  task_name: "Last enriched by this job",
  entity_lookup: "Most recent enrichment job",
};

export function EntityTinaTrace({
  entityType,
  entityId,
  metadata,
  enrichment,
  disableLookup,
}: EntityTinaTraceProps) {
  const itemsRepo = useItemsRepository();
  const runsRepo = useRunsRepository();
  const [costOpen, setCostOpen] = React.useState(false);

  const { pointer, isLoading: pointerLoading } = useEntityTrace({
    entityType,
    entityId,
    metadata,
    taskName: readTaskName(enrichment),
    disableLookup,
  });

  const tinaItemId = pointer?.itemId;

  const itemQuery = useQuery({
    queryKey: ["tina", "items", tinaItemId],
    queryFn: () => itemsRepo.get(tinaItemId!),
    enabled: !!tinaItemId,
    staleTime: 60_000,
  });

  const resolvedRunId = itemQuery.data?.run_id ?? pointer?.runId;

  const runQuery = useQuery({
    queryKey: ["tina", "runs", resolvedRunId],
    queryFn: () => runsRepo.get(resolvedRunId!),
    enabled: !!resolvedRunId,
    staleTime: 60_000,
  });

  if (pointerLoading || (!!tinaItemId && itemQuery.isLoading)) {
    return <TraceCardSkeleton />;
  }

  // No pointer at all: Tina neither created nor enriched this entity - or it
  // predates the section 17 stamp and has not been enriched since. Silence is
  // the honest rendering; the card is an affordance, not a status.
  if (!pointer) return null;

  if (itemQuery.isError || !itemQuery.data) {
    return (
      <TraceCardError
        message={itemErrorMessage(itemQuery.error)}
        sourceUrl={pointer.url}
      />
    );
  }

  const item = itemQuery.data;
  const isEnrichmentJob = isEnrichmentContentType(item.content_type);
  const sourceSite = pointer.site ?? item.site;
  const sourceUrl = pointer.url ?? item.url ?? undefined;
  const run = runQuery.data;
  const accent = statusAccentOf(item.status);

  const confidence = parseConfidence(item.final_confidence);
  const totalCost = parseFloatOrNull(run?.summary?.total_cost ?? null);
  const duration = computeDuration(item.created_at, item.finished_at);

  return (
    // `shrink-0` because every entity preview mounts this inside a
    // `flex flex-col max-h-[calc(100vh-108px)] overflow-y-auto` aside. Flex
    // items shrink by default, so once the sidebar's content exceeded that
    // height the browser compressed THIS card rather than scrolling the aside —
    // and `overflow-hidden` then clipped everything below the Source block.
    <Card className="relative shrink-0 overflow-hidden">
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-1",
          accent.stripe,
        )}
      />

      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3 pl-7">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
              accent.iconWrap,
            )}
          >
            <Network
              className={cn("h-4 w-4", accent.iconColor)}
              strokeWidth={2.25}
            />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {isEnrichmentJob ? "Tina Enrichment" : "Tina Trace"}
            </span>
            <span className="text-sm font-semibold leading-tight tracking-tight">
              {accent.label}
            </span>
            <span className="text-[10px] leading-tight text-muted-foreground/80">
              {PROVENANCE_LABEL[pointer.source]}
              {isEnrichmentJob
                ? ` \u00b7 ${contentTypeLabel(item.content_type)}`
                : ""}
            </span>
          </div>
        </div>
        <Link
          to={`/tina/items/show/${item.id}`}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          Open
          <ArrowUpRight className="h-3 w-3" />
        </Link>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 pb-5 pl-7">
        <SectionBlock icon={<GitBranch className="h-3 w-3" />} label="Source">
          <div className="flex flex-col gap-0.5 text-xs">
            <div className="flex items-center gap-1.5">
              {sourceSite ? (
                <span className="font-mono font-medium text-foreground">
                  {sourceSite}
                </span>
              ) : (
                <span className="text-muted-foreground/60">—</span>
              )}
            </div>
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex max-w-full items-center gap-1 truncate text-muted-foreground hover:text-foreground"
                title={sourceUrl}
              >
                <span className="truncate">{urlPath(sourceUrl)}</span>
                <ExternalLink className="h-3 w-3 shrink-0 opacity-60 group-hover:opacity-100" />
              </a>
            )}
          </div>
        </SectionBlock>

        <div
          className={cn(
            "grid gap-3",
            item.hitl_resolved_by ? "grid-cols-3" : "grid-cols-2",
          )}
        >
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Confidence</SectionLabel>
            {confidence != null ? (
              <ConfidenceMeter value={confidence} />
            ) : (
              <span className="text-xs text-muted-foreground/60">—</span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Outcome</SectionLabel>
            {item.final_outcome ? (
              <Badge
                variant="outline"
                className="w-fit text-[11px] font-medium capitalize"
              >
                {item.final_outcome.replace(/_/g, " ")}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground/60">—</span>
            )}
          </div>
          {/* Rendered only when Tina recorded WHO resolved the park. A NULL uid
              means "unknown" - a pre-0027 resolution was never backfilled - so
              an empty cell here is not a claim that nothing was reviewed. */}
          {item.hitl_resolved_by && (
            <SectionBlock
              icon={<UserCheck className="h-3 w-3" />}
              label="Resolved by"
            >
              <ResolvedByChip
                uid={item.hitl_resolved_by}
                resolvedAt={item.hitl_resolved_at}
              />
            </SectionBlock>
          )}
        </div>

        <Separator className="my-1" />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <SectionLabel>Run</SectionLabel>
            {run?.run?.status && <RunStatusBadge status={run.run.status} />}
          </div>
          {resolvedRunId && (
            <Link
              to={`/tina/runs/show/${resolvedRunId}`}
              className="group flex items-center gap-2 rounded-md bg-muted/40 px-2.5 py-1.5 font-mono text-[11px] text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
            >
              <span className="text-muted-foreground/70">run</span>
              <span className="font-semibold tracking-tight">
                {shortId(resolvedRunId)}
              </span>
              <ArrowUpRight className="ml-auto h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          )}

          {totalCost != null && resolvedRunId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCostOpen(true)}
              className="h-8 justify-between gap-2 text-xs"
            >
              <span className="inline-flex items-center gap-1.5">
                <CircleDollarSign className="h-3.5 w-3.5 text-indigo-500" />
                Run cost
              </span>
              <span className="font-mono font-semibold tabular-nums">
                {formatUsdCost(totalCost)}
              </span>
            </Button>
          )}
        </div>

        {item.discovered_count != null && item.discovered_count > 0 && (
          <>
            <Separator className="my-1" />
            <Link
              to={`/tina/items/show/${item.id}`}
              className="group flex items-center justify-between rounded-md border border-dashed border-border bg-background px-2.5 py-2 text-xs transition-colors hover:border-foreground/30 hover:bg-accent/30"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-violet-500/10 px-1.5 font-mono text-[10px] font-bold text-violet-600 dark:text-violet-400">
                  {item.discovered_count}
                </span>
                <span className="text-foreground">
                  {item.discovered_count === 1 ? "discovery" : "discoveries"}
                </span>
              </div>
              <ArrowUpRight className="h-3 w-3 text-muted-foreground group-hover:text-foreground" />
            </Link>
          </>
        )}

        <div className="flex items-center gap-1.5 pt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground/80">
          <Clock className="h-3 w-3" />
          <span className="tabular-nums">
            {moment(item.created_at).format("D MMM HH:mm")}
          </span>
          {duration && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className="tabular-nums">{duration}</span>
            </>
          )}
        </div>
      </CardContent>

      {resolvedRunId && (
        <CostBreakdownDialog
          runId={resolvedRunId}
          open={costOpen}
          onOpenChange={setCostOpen}
        />
      )}
    </Card>
  );
}

/**
 * `STATUS_ACCENT` is keyed on the `ItemStatus` union, but `item.status` is an
 * unvalidated cast off `GET /events` — a status Tina adds after this deploy
 * misses every key, and the unguarded `accent.stripe` below it is a white
 * screen on all nine entity pages. Falls back to a neutral accent that prints
 * the raw status as its label, which is the useful thing to see.
 */
function statusAccentOf(status: ItemStatus): StatusAccent {
  return (
    STATUS_ACCENT[status] ?? {
      label: status,
      icon: Network,
      stripe: "bg-muted-foreground/40",
      iconWrap: "bg-muted",
      iconColor: "text-muted-foreground",
    }
  );
}

function SectionBlock({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span className="opacity-70">{icon}</span>
        <SectionLabel>{label}</SectionLabel>
      </div>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </span>
  );
}

function TraceCardSkeleton() {
  return (
    // `shrink-0` for the same reason as the loaded card: a squeezed skeleton
    // makes the sidebar jump when the real card lands at full height.
    <Card className="relative shrink-0 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-muted-foreground/20"
      />
      <CardHeader className="pb-3 pl-7">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex flex-col gap-1">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-5 pl-7">
        <Skeleton className="h-10" />
        <Skeleton className="h-6" />
        <Skeleton className="h-16" />
      </CardContent>
    </Card>
  );
}

function TraceCardError({
  message,
  sourceUrl,
}: {
  message: string;
  sourceUrl?: string;
}) {
  return (
    // `shrink-0` — an error card that gets clipped hides the very message it
    // exists to show.
    <Card className="relative shrink-0 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-amber-500"
      />
      <CardHeader className="pb-3 pl-7">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-500/10">
            <AlertTriangle
              className="h-4 w-4 text-amber-600 dark:text-amber-400"
              strokeWidth={2.25}
            />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Tina Trace
            </span>
            <span className="text-sm font-semibold tracking-tight">
              Couldn't load trace
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-5 pl-7">
        <p className="text-xs leading-relaxed text-muted-foreground">
          {message}
        </p>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            Source URL
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function urlPath(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname + parsed.search;
    return path === "/" ? parsed.hostname : path;
  } catch {
    return url;
  }
}

function parseConfidence(value: string | null | undefined): number | undefined {
  if (value == null) return undefined;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseFloatOrNull(value: string | null): number | null {
  if (value == null) return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function computeDuration(start: string, end: string | null): string | null {
  if (!end) return null;
  const ms = moment(end).diff(moment(start));
  if (ms < 0) return null;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

function itemErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Failed to load Tina item details.";
}
